import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type AccidentViolationItem, AccidentViolationSource } from '@/models/dashboard'
import { CameraMedia } from './CameraMedia'
import { DetailRow } from './DetailRow'
import { LocationMap, LocationMapByAddress } from './LocationMap'
import { maskVin, SOURCE_LABELS } from './violationDisplay'

function longDate(dateStr: string | null | undefined): string | undefined {
  if (!dateStr) return undefined
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Detail for an FMCSA (CAB) crash/inspection or a telematics safety event —
 * only reachable when VIOLATIONS_INCLUDE_FMCSA / VIOLATIONS_INCLUDE_TELEMATICS
 * is on for the org.
 */
export function SafetyEventDetail({ item }: { item: AccidentViolationItem }) {
  const isTelematics = item.source === AccidentViolationSource.TERMINAL
  const eventId = isTelematics ? item.id.replace('terminal-', '') : null

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow label="Date" value={longDate(item.date)} />
            <DetailRow label="Time" value={item.time} />
            <DetailRow label="Type" value={item.type.replace(/_/g, ' ')} />
            <DetailRow label="Source" value={SOURCE_LABELS[item.source]} />
            <DetailRow label="Driver" value={item.driverName} />
            <DetailRow
              label="Vehicle VIN"
              value={item.vehicleVin ? `VIN ${maskVin(item.vehicleVin)}` : undefined}
            />
            <DetailRow label="Location" value={item.location} />
            <DetailRow
              label="City / State"
              value={
                item.city && item.state ? `${item.city}, ${item.state}` : item.city || item.state || undefined
              }
            />
            {item.fatalities != null && item.fatalities > 0 && (
              <DetailRow label="Fatalities" value={String(item.fatalities)} />
            )}
            {item.injuries != null && item.injuries > 0 && (
              <DetailRow label="Injuries" value={String(item.injuries)} />
            )}
            {item.towAway && <DetailRow label="Tow away" value="Yes" />}
            {item.vehiclesInCrash != null && item.vehiclesInCrash > 0 && (
              <DetailRow label="Vehicles in crash" value={String(item.vehiclesInCrash)} />
            )}
            <DetailRow label="BASIC category" value={item.basicCategory?.replace(/_/g, ' ')} />
            {item.inspectionLevel != null && (
              <DetailRow label="Inspection level" value={String(item.inspectionLevel)} />
            )}
            {item.violationOos && <DetailRow label="Violation OOS" value="Yes" />}
            {item.inspectionOos && <DetailRow label="Inspection OOS" value="Yes" />}
            {item.latitude != null && item.longitude != null && (
              <DetailRow
                label="Coordinates"
                value={`${item.latitude.toFixed(6)}, ${item.longitude.toFixed(6)}`}
              />
            )}
          </CardContent>
        </Card>

        {item.latitude != null && item.longitude != null ? (
          <LocationMap latitude={item.latitude} longitude={item.longitude} />
        ) : (
          (item.city || item.state) && (
            <LocationMapByAddress location={item.location} city={item.city} state={item.state} />
          )
        )}
      </div>

      {eventId && <CameraMedia eventId={eventId} />}
    </>
  )
}
